import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowRight, Check, LogIn, ExternalLink, ShieldAlert } from 'lucide-react';

/**
 * tcn.noirock.app/ — Public install landing for the TCN Agent app.
 *
 * Routed via src/pages/home/index.jsx: when getHostTenantSlug() === 'tcn', we
 * render this page instead of the apex multi-space landing. English-only by
 * product decision — TCN testers are a curated group, no i18n needed yet.
 *
 * Surfaces:
 *  - Sign-in CTA at the top for returning users who already installed the app
 *    (we used to /login-authentication straight from /, so this preserves their
 *    1-click path without making the landing useless for new testers).
 *  - iOS section: TestFlight join link (https://testflight.apple.com/join/GvkjW1yR)
 *    + 4 steps. The TestFlight URL on iOS Safari opens the TestFlight app via
 *    universal link; on desktop it shows Apple's web install page.
 *  - Android section: direct APK download (public/downloads/tcn-agent.apk,
 *    committed alongside this page) + sideload warning + 4 steps. APK is the
 *    Capacitor debug build — release/Play Store will replace this later.
 */

const TestFlightUrl = 'https://testflight.apple.com/join/GvkjW1yR';
const ApkUrl = '/downloads/tcn-agent.apk';
const ApkSizeLabel = '14 MB';

// Inline Apple + Android SVG logos. Lucide's `Apple` icon is a fruit, not the
// Apple Inc. mark — using brand-recognizable silhouettes makes the section
// scannable in 1 second on a phone.
const AppleLogo = ({ size = 28, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const AndroidLogo = ({ size = 28, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden>
    <path d="M17.523 15.34c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m-11.05 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m11.41-6.02l2-3.46a.42.42 0 0 0-.16-.57.42.42 0 0 0-.56.16l-2.03 3.5C15.59 8.24 13.85 7.85 12 7.85s-3.59.4-5.14 1.1L4.84 5.45a.42.42 0 0 0-.57-.16.42.42 0 0 0-.15.57l2 3.46C2.69 11.19.34 14.66 0 18.76h24c-.34-4.1-2.69-7.57-6.12-9.44" />
  </svg>
);

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span
      className="shrink-0 inline-flex items-center justify-center"
      style={{
        width: 22,
        height: 22,
        borderRadius: 99,
        background: 'rgba(34,211,238,0.14)',
        border: '1px solid rgba(34,211,238,0.35)',
        color: '#22d3ee',
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        marginTop: 1,
      }}
    >
      {n}
    </span>
    <span className="text-white/70 text-sm leading-relaxed">{children}</span>
  </li>
);

const TcnInstall = () => {
  return (
    <div
      className="flex flex-col"
      style={{
        minHeight: '100dvh',
        backgroundColor: '#000000',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        color: '#f5f5f7',
        // Safe-area on both ends so the chrome doesn't slip under the notch on
        // mobile Safari + matches the rest of TCN's edge-to-edge layout.
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Subtle cyan glow — keeps TCN identity without overpowering the content */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, #22d3ee 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Header — logo left, "Sign in" CTA right (preserves the previous direct
          login path for returning users) */}
      <header className="relative z-10 flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <img src="/logo-tcn.png" alt="TCN Agent" className="w-7 h-7 rounded-lg" />
          <span className="text-white/60 text-xs font-semibold tracking-wide">TCN AGENT</span>
        </div>
        <Link
          to="/login-authentication"
          className="inline-flex items-center gap-1.5 text-xs font-semibold no-underline rounded-lg px-3 py-2 transition-colors"
          style={{
            color: '#22d3ee',
            border: '1px solid rgba(34,211,238,0.35)',
            background: 'rgba(34,211,238,0.06)',
          }}
        >
          <LogIn className="w-3.5 h-3.5" />
          Sign in
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-5 pt-6 pb-2 flex flex-col items-center text-center">
        <img
          src="/logo-tcn.png"
          alt=""
          className="w-16 h-16 rounded-2xl mb-5"
          style={{ boxShadow: '0 0 40px rgba(34,211,238,0.25)' }}
        />
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight"
          style={{ fontFamily: 'Sora, Inter, sans-serif' }}
        >
          Install TCN Agent
        </h1>
        <p className="text-white/60 text-sm sm:text-base mt-3 max-w-md leading-relaxed">
          The TCN Agent app is currently in private testing. Pick your platform below — both
          builds run the same backend, so your account works across devices.
        </p>
      </section>

      {/* Two install cards — stacked on mobile, side-by-side on md+ */}
      <main className="relative z-10 flex-1 px-5 py-8">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ── iOS / TestFlight ─────────────────────────────────────────── */}
          <article
            className="rounded-2xl p-6 border flex flex-col"
            style={{
              background: '#0e0e10',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center gap-3 mb-1">
              <div
                className="inline-flex items-center justify-center rounded-xl"
                style={{
                  width: 44,
                  height: 44,
                  background: 'rgba(255,255,255,0.06)',
                  color: '#f5f5f7',
                }}
              >
                <AppleLogo size={26} />
              </div>
              <div>
                <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Sora, Inter, sans-serif' }}>
                  iPhone &amp; iPad
                </h2>
                <p className="text-white/40 text-xs mt-0.5">via Apple TestFlight (beta program)</p>
              </div>
            </div>

            <ol className="mt-5 space-y-3 flex-1">
              <Step n={1}>
                Install <span className="font-semibold text-white/90">TestFlight</span> from the
                App Store if you don't already have it. It's free and made by Apple — required to
                run any iOS beta.
              </Step>
              <Step n={2}>
                On your iPhone or iPad, tap the button below. It will open in TestFlight and show
                the TCN Agent invite.
              </Step>
              <Step n={3}>
                Tap <span className="font-semibold text-white/90">Accept</span>, then{' '}
                <span className="font-semibold text-white/90">Install</span> at the top right.
                TestFlight downloads the build (≈ 25 MB).
              </Step>
              <Step n={4}>
                TCN Agent now sits on your home screen. Open it and sign in with the credentials
                your manager shared with you.
              </Step>
            </ol>

            <a
              href={TestFlightUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold text-sm no-underline transition-transform"
              style={{
                background: '#22d3ee',
                color: '#000000',
              }}
            >
              <ExternalLink className="w-4 h-4" />
              Open TestFlight invite
            </a>

            <p className="text-white/30 text-[11px] mt-3 text-center">
              Tip: if the link opens in Safari instead of TestFlight, copy it and paste it in
              TestFlight's "Redeem" screen.
            </p>
          </article>

          {/* ── Android / APK ────────────────────────────────────────────── */}
          <article
            className="rounded-2xl p-6 border flex flex-col"
            style={{
              background: '#0e0e10',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center gap-3 mb-1">
              <div
                className="inline-flex items-center justify-center rounded-xl"
                style={{
                  width: 44,
                  height: 44,
                  background: 'rgba(61,220,151,0.10)',
                  color: '#3ddc97',
                }}
              >
                <AndroidLogo size={26} />
              </div>
              <div>
                <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Sora, Inter, sans-serif' }}>
                  Android
                </h2>
                <p className="text-white/40 text-xs mt-0.5">direct APK install (sideload)</p>
              </div>
            </div>

            <ol className="mt-5 space-y-3 flex-1">
              <Step n={1}>
                On your Android phone, tap{' '}
                <span className="font-semibold text-white/90">Download APK</span> below. The file
                ({ApkSizeLabel}) saves to your Downloads folder.
              </Step>
              <Step n={2}>
                Open the downloaded file. Android may ask you to{' '}
                <span className="font-semibold text-white/90">allow installs from this source</span>
                . Confirm — it's the standard sideload prompt for non-Play-Store apps.
              </Step>
              <Step n={3}>
                Tap <span className="font-semibold text-white/90">Install</span>. If Play Protect
                flags the app as "unrecognized", tap{' '}
                <span className="font-semibold text-white/90">Install anyway</span> — that's
                expected for a private beta that isn't on the Play Store.
              </Step>
              <Step n={4}>
                TCN Agent appears in your app drawer. Open it and sign in with the credentials
                your manager shared with you.
              </Step>
            </ol>

            <a
              href={ApkUrl}
              download
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold text-sm no-underline transition-transform"
              style={{
                background: '#3ddc97',
                color: '#000000',
              }}
            >
              <Download className="w-4 h-4" />
              Download APK ({ApkSizeLabel})
            </a>

            <div
              className="mt-3 flex items-start gap-2 rounded-lg p-2.5"
              style={{
                background: 'rgba(255,181,71,0.06)',
                border: '1px solid rgba(255,181,71,0.18)',
              }}
            >
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#ffb547' }} />
              <p className="text-[11px] leading-snug" style={{ color: '#e0c68a' }}>
                This is the <strong>debug</strong> build for testing. A Play Store release will
                replace it once the app exits beta.
              </p>
            </div>
          </article>
        </div>

        {/* Help / already installed footer block */}
        <div className="max-w-4xl mx-auto mt-6">
          <div
            className="rounded-2xl border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            style={{
              background: '#0e0e10',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center gap-3 flex-1">
              <Check className="w-5 h-5 shrink-0" style={{ color: '#22d3ee' }} />
              <div>
                <p className="font-semibold text-sm">Already installed?</p>
                <p className="text-white/50 text-xs mt-0.5">
                  Open TCN Agent on your phone and sign in — or use the web console here.
                </p>
              </div>
            </div>
            <Link
              to="/login-authentication"
              className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold no-underline whitespace-nowrap"
              style={{
                background: 'rgba(34,211,238,0.10)',
                border: '1px solid rgba(34,211,238,0.35)',
                color: '#22d3ee',
              }}
            >
              Sign in on the web
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-5 py-6 flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          <Link to="/privacy" className="text-white/30 text-xs no-underline hover:text-white/50">
            Privacy
          </Link>
          <span className="text-white/10 text-xs">·</span>
          <Link to="/terms" className="text-white/30 text-xs no-underline hover:text-white/50">
            Terms
          </Link>
          <span className="text-white/10 text-xs">·</span>
          <Link to="/delete-account" className="text-white/30 text-xs no-underline hover:text-white/50">
            Delete account
          </Link>
        </div>
        <p className="text-white/20 text-xs">© 2026 NoiRock — TCN Agent</p>
      </footer>
    </div>
  );
};

export default TcnInstall;
